/**
 * Read-only souhrny vlastníků / parcel / kontaktu pro Informace (web + parita mobile).
 * Stejná logika jako karty ve Smlouvách — bez druhé mapovací vrstvy.
 */

import { firstOwnerContactFromRows } from './owner-contact.util';

export type ContractOwnerDisplayRow = {
  jmenoNazev?: string | null;
  email?: string | null;
  telefon?: string | null;
};

export type ContractParcelDisplayRow = {
  parcelniCislo?: string | null;
  parcelaLabel?: string | null;
  label?: string | null;
  kuNazev?: string | null;
};

/**
 * True when value is only a count label („12 vlastníků“ / „3 vlastníci“), not real names.
 * ACQ report / legacy fields sometimes store a count instead of KN names.
 */
export function isOwnerCountSummaryLabel(value: string | null | undefined): boolean {
  const t = (value ?? '').trim().toLowerCase();
  if (!t) return false;
  return /^\d+\s+vlastník(?:a|ů|y)?$/.test(t) || /^\d+\s+vlastníci$/.test(t);
}

/**
 * Jména vlastníků oddělená čárkou (parita web `ownersSummary` / budova Informace).
 * Always lists names — never collapses to „N vlastníků“.
 */
export function formatContractOwnersSummary(
  owners: Array<{ jmenoNazev?: string | null }> | null | undefined,
): string {
  return formatBudovaOwnerNamesDisplay(
    (owners ?? []).map((o) => (o.jmenoNazev ?? '').trim()),
  );
}

/**
 * Shared budova owner display: unique non-empty names, comma-separated.
 * Skips count-summary labels. Callers wrap in UI when many.
 */
export function formatBudovaOwnerNamesDisplay(
  names: Array<string | null | undefined> | null | undefined,
): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of names ?? []) {
    const name = (raw ?? '').trim();
    if (!name || isOwnerCountSummaryLabel(name)) continue;
    const key = name.toLocaleLowerCase('cs');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out.length ? out.join(', ') : '';
}

/** Parcely + k.ú. (parita web `parcelsSummary`). */
export function formatContractParcelsSummary(
  parcels: ContractParcelDisplayRow[] | null | undefined,
): string {
  const labels = (parcels ?? [])
    .map((p) => {
      const parcela = (
        p.parcelaLabel ||
        p.label ||
        p.parcelniCislo ||
        ''
      ).trim();
      if (!parcela) return '';
      const ku = (p.kuNazev ?? '').trim();
      const parts = [parcela, ku ? `k.ú. ${ku}` : null].filter(Boolean);
      return parts.join(', ');
    })
    .filter(Boolean);
  return labels.length ? labels.join('; ') : '';
}

/** E-mail · telefon (nebo jen jedno) — null pokud nic. */
export function formatOwnerContactDisplay(
  email?: string | null,
  phone?: string | null,
): string | null {
  const e = (email ?? '').trim();
  const p = (phone ?? '').trim();
  if (e && p) return `${e} · ${p}`;
  if (e) return e;
  if (p) return p;
  return null;
}

export interface BudovaKnInfoSummary {
  ownersSummary: string | null;
  parcelsSummary: string | null;
  contactSummary: string | null;
}

export interface BuildBudovaKnInfoSummaryInput {
  /** Preferovaný snapshot `document_parties`. */
  partiesOwners?: ContractOwnerDisplayRow[] | null;
  partiesParcels?: ContractParcelDisplayRow[] | null;
  /** Seznamové vlastnici/parcely ze smlouvy. */
  listOwners?: ContractOwnerDisplayRow[] | null;
  listParcels?: ContractParcelDisplayRow[] | null;
  ownerEmail?: string | null;
  ownerPhone?: string | null;
  /** ACQ report fallback (rawData / report fields). */
  reportOwnerName?: string | null;
  reportContactEmail?: string | null;
  reportContactPhone?: string | null;
}

/**
 * Nejlepší dostupný KN + kontakt pro Informace / detail budovy.
 * Priorita: parties → list owners/parcels → ACQ report; kontakt: parties/list → legacy sloupce → report.
 */
export function buildBudovaKnInfoSummary(
  input: BuildBudovaKnInfoSummaryInput,
): BudovaKnInfoSummary {
  const partyOwners = input.partiesOwners ?? [];
  const listOwners = input.listOwners ?? [];
  const ownersForNames = partyOwners.length ? partyOwners : listOwners;
  let ownersSummary = formatContractOwnersSummary(ownersForNames) || null;
  if (!ownersSummary) {
    const reportName = (input.reportOwnerName ?? '').trim();
    // Ignore count-only ACQ placeholders („12 vlastníků“) — prefer empty over fake summary.
    ownersSummary =
      reportName && !isOwnerCountSummaryLabel(reportName) ? reportName : null;
  }

  const partyParcels = input.partiesParcels ?? [];
  const listParcels = input.listParcels ?? [];
  const parcelsForLabels = partyParcels.length ? partyParcels : listParcels;
  const parcelsSummary = formatContractParcelsSummary(parcelsForLabels) || null;

  const fromOwners = firstOwnerContactFromRows([partyOwners, listOwners]);
  const email =
    fromOwners.email ||
    (input.ownerEmail ?? '').trim() ||
    (input.reportContactEmail ?? '').trim();
  const phone =
    fromOwners.phone ||
    (input.ownerPhone ?? '').trim() ||
    (input.reportContactPhone ?? '').trim();
  const contactSummary = formatOwnerContactDisplay(email, phone);

  return { ownersSummary, parcelsSummary, contactSummary };
}

/** Preferuj souhlas s parties/owners; jinak první smlouva s daty. */
export function pickBestContractForKnInfo<
  T extends {
    contractType?: string | null;
    parties?: {
      owners?: unknown[] | null;
      parcels?: unknown[] | null;
    } | null;
    owners?: unknown[] | null;
    parcels?: unknown[] | null;
    vlastnici?: unknown[] | null;
    parcely?: unknown[] | null;
  },
>(contracts: T[]): T | null {
  if (!contracts.length) return null;
  const score = (c: T): number => {
    const type = (c.contractType ?? '').toLowerCase();
    const typeBonus = type === 'souhlas' ? 100 : 0;
    const partyOwners = c.parties?.owners?.length ?? 0;
    const partyParcels = c.parties?.parcels?.length ?? 0;
    const listOwners = (c.owners ?? c.vlastnici)?.length ?? 0;
    const listParcels = (c.parcels ?? c.parcely)?.length ?? 0;
    return typeBonus + partyOwners * 10 + partyParcels * 5 + listOwners * 2 + listParcels;
  };
  return [...contracts].sort((a, b) => score(b) - score(a))[0] ?? null;
}
